<script setup>
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },

  label: {
    type: String,
    default: undefined,
  },
  type: {
    type: String,
    default: 'text',
  },
  name: {
    type: String,
    required: true,
  },
  autocomplete: {
    type: String,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  maxLength: {
    type: Number,
    default: undefined,
  },
  pattern: {
    type: String,
    default: undefined,
  },
  inputMode: {
    type: String,
    default: undefined,
  },

  clearable: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  preventFocusScroll: {
    type: Boolean,
    default: true,
  },
  autocapitalize: {
    type: String,
    default: undefined,
  },

  theme: {
    type: String,
    default: 'gray',
    validator(value) {
      return [
        'gray',
        'purple',
        'underline',
        'underline-red',
        'outlined',
        'outlined-2',
        'outlined-2-red',
        'outlined-2-orange',
      ].includes(value);
    },
  },
  textAlign: {
    type: String,
    default: 'left',
    validator(value) {
      return ['left', 'center'].includes(value);
    },
  },
  fontSize: {
    type: String,
    default: 'md',
    validator(value) {
      return ['md', 'lg'].includes(value);
    },
  },
  preserveLeft: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },

  setTabIndex: {
    type: Boolean,
    default: true,
  },
  tabindex: {
    type: Number,
    default: 1,
  },

  // borders/border-radius control for inputs with collision
  multiInputPositioning: {
    type: String,
    default: undefined,
    validator(value) {
      return ['left', 'center', 'right'].includes(value);
    },
  },
  transparentSideBorders: {
    type: Boolean,
    default: false,
  },
  validateOnInput: {
    type: Boolean,
    default: true,
  },
  validateOnBlur: {
    type: Boolean,
    default: false,
  },

  // fix for ios zoom on focus when font-size < 16px
  lockMobileFontSize: {
    type: Boolean,
    default: false,
  },

  validation: {
    type: [String, Object],
    default: undefined,
  },
});
const emit = defineEmits(['update:modelValue', 'clear', 'blur', 'focus']);

const localValue = useVModel(props, 'modelValue', emit);

const isTextarea = computed(() => props.type === 'textarea');
const component = computed(() => (isTextarea.value ? 'textarea' : 'input'));

function clear() {
  emit('update:modelValue', '');
  emit('clear');
  focus();
}

// focus
const fieldRef = ref(null);
function focus() {
  fieldRef.value.focus({ preventScroll: props.preventFocusScroll });
}
onMounted(() => {
  if (props.autofocus) focus();
});

const isFocused = ref(false);

function handleFocus() {
  isFocused.value = true;
  emit('focus');
}
function handleBlur() {
  isFocused.value = false;
  emit('blur');
}

// placeholder
const isPlaceholderMovable = computed(() =>
  ['outlined-2', 'outlined-2-red', 'outlined-2-orange'].includes(props.theme),
);

defineExpose({ focus });
</script>

<template>
  <div class="form-field-input" :class="`form-field-input--theme--${theme}`">
    <div v-if="label" class="form-field-input__label">
      <span class="form-field-input__font form-field-input__font--label">
        {{ label }}
      </span>
    </div>

    <VeeField
      v-slot="{ field, errors }"
      v-model.trim="localValue"
      :name="name"
      :label="placeholder"
      :rules="validation"
      :validate-on-model-update="false"
      :validate-on-input="validateOnInput"
      :validate-on-blur="validateOnBlur"
    >
      <div class="form-field-input__wrapper">
        <component
          :is="component"
          ref="fieldRef"
          v-bind="field"
          :type="type"
          :autocomplete="autocomplete"
          :placeholder="placeholder"
          class="form-field-input__field"
          :disabled="disabled"
          :autocapitalize="autocapitalize"
          :maxlength="maxLength"
          :pattern="pattern"
          :inputmode="inputMode"
          :class="{
            'form-field-input__field--clearable': clearable && localValue,
            'form-field-input__field--invalid': errors.length,
            'form-field-input__field--preserve-left': preserveLeft,
            'form-field-input__field--active': isFocused,
            'form-field-input__field--lock-font-size': lockMobileFontSize,
            [`form-field-input__field--font-size--${fontSize}`]: fontSize,
            [`form-field-input__field--position--${multiInputPositioning}`]:
              !!multiInputPositioning,
            [`form-field-input__field--transparent-borders--${multiInputPositioning}`]:
              transparentSideBorders && !!multiInputPositioning,
          }"
          :tabindex="setTabIndex ? tabindex : undefined"
          @focus="handleFocus"
          @blur="handleBlur"
        />

        <span
          v-if="placeholder && isPlaceholderMovable && !preserveLeft"
          class="form-field-input__placeholder"
          :class="{
            'form-field-input__placeholder--shifted': localValue || isFocused,
            'form-field-input__placeholder--invalid': errors.length,
            'form-field-input__placeholder--active': isFocused,
          }"
        >
          {{ placeholder }}
        </span>

        <Transition name="fade">
          <button
            v-if="clearable && localValue"
            type="button"
            class="form-field-input__button"
            @click="clear"
          >
            <CIcon class="form-field-input__icon" name="trash" />
          </button>
        </Transition>
      </div>

      <span v-if="errors.length" class="form-field-input__error">
        <span class="form-field-input__font form-field-input__font--error">
          {{ errors[0] }}
        </span>
      </span>
    </VeeField>
  </div>
</template>

<style lang="scss" scoped>
.form-field-input {
  $parent: &;

  display: flex;
  flex-direction: column;

  &__font {
    &--label {
      font-size: em(18);
      font-weight: 400;
      line-height: math.div(24, 18);
    }

    &--error {
      font-size: em(12);
      line-height: math.div(13, 12);
    }
  }

  &__icon {
    @include box(em(28));

    flex-shrink: 0;
    color: $color-main-1;
  }

  &__label {
    padding-left: em(13);
    margin-bottom: em(7);
  }

  &__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__field {
    flex-grow: 1;
    padding: em(14, 18) em(13, 18);
    font-size: em(18);
    font-weight: 400;
    line-height: math.div(24, 18);
    border-radius: em(8);

    /* stylelint-disable */
    text-align: v-bind(textAlign);
    /* stylelint-enable */

    &::placeholder {
      color: $color-gray-4;
    }

    &--clearable {
      padding-right: em(44, 18);
    }

    &--preserve-left {
      padding-left: em(44, 18);
    }
  }

  &__placeholder {
    @include line-clamp(2);

    max-width: 90%;
    position: absolute;
    pointer-events: none;
  }

  &__button {
    position: absolute;
    top: 50%;
    right: em(8);
    transform: translateY(-50%);
  }

  &__error {
    display: block;
    margin-top: em(4);
    margin-left: em(13);
    color: $color-error;
  }

  &--theme {
    &--gray {
      #{$parent} {
        &__field {
          background-color: $color-white;
          border: 1px solid $color-gray-8;
        }
      }
    }

    &--purple {
      #{$parent} {
        &__field {
          background-color: $color-white;
          border: 1px solid $color-main-1;
        }
      }
    }

    &--underline {
      #{$parent} {
        &__field {
          padding: em(5, 18) em(12, 18);
          color: $color-main-5;
          border-bottom: 1px solid $color-gray-8;
          border-radius: 0;

          &--invalid {
            border-color: $color-error;
          }

          &--preserve-left {
            padding-left: em(80, 18);
          }

          &:disabled {
            opacity: 0.7;
          }
        }

        &__error {
          margin-left: em(12);
        }
      }
    }

    &--underline-red {
      #{$parent} {
        &__field {
          padding: em(5, 18) em(12, 18);
          color: $color-main-5;
          border-bottom: 1px solid $color-error;
          border-radius: 0;

          &--preserve-left {
            padding-left: em(80, 18);
          }

          &:disabled {
            opacity: 0.7;
          }
        }

        &__error {
          margin-left: em(12);
        }
      }
    }

    &--outlined {
      #{$parent} {
        &__field {
          padding: em(5, 17);
          font-size: em(17);
          text-align: center;
          background-color: $color-white;
          border: 1px solid $color-gray-8;
          border-radius: em(7, 17);
          color: $color-main-5;
          transition: border-color $time-3;
          min-height: em(38);

          &:disabled {
            opacity: 0.7;
          }

          &--invalid {
            border-color: $color-error;
          }
        }
      }
    }

    &--outlined-2,
    &--outlined-2-red,
    &--outlined-2-orange {
      #{$parent} {
        &__field {
          background-color: $color-white;
          border: 1px solid $color-gray-8;
          color: $color-black;
          transition: border-color $time-3;

          &::placeholder {
            opacity: 0;
          }

          &:disabled {
            opacity: 0.7;
          }

          &--invalid {
            border-color: $color-error;
          }

          &--lock-font-size {
            @media screen and (width < 390px) {
              font-size: 16px !important;
            }
          }

          &--font-size {
            &--md {
              font-size: em(16);
              padding: em(7, 16) em(22, 16) em(7, 16) em(11, 16);
              border-radius: em(6, 16);
              line-height: math.div(24, 16);
              min-height: em(43, 16);
            }

            &--lg {
              font-size: em(36);
              padding: em(7, 36) em(22, 36) em(7, 36) em(11, 36);
              border-radius: em(6, 36);
              line-height: 1;
              min-height: em(63, 36);
            }
          }

          &--position {
            &--left {
              border-radius: em(6, 16) 0 0 em(6, 16);
            }

            &--center {
              border-radius: 0;
            }

            &--right {
              border-radius: 0 em(6, 16) em(6, 16) 0;
            }
          }

          &--font-size--lg &--position {
            &--left {
              border-radius: em(6, 36) 0 0 em(6, 36);
            }

            &--right {
              border-radius: 0 em(6, 36) em(6, 36) 0;
            }
          }

          &--transparent-borders {
            &--left {
              border-right-color: transparent !important;
            }

            &--right {
              border-left-color: transparent !important;
            }
          }

          &--preserve-left {
            padding-left: em(80);
          }

          &--active {
            border-color: $color-main-1 !important;
          }
        }

        &__placeholder {
          top: 50%;
          left: em(10, 16);
          transform: translateY(-50%);
          background-color: $color-white;
          color: $color-gray-4;
          padding: 0 1px;
          transition:
            color $time-3,
            top $time-3;

          &--shifted {
            top: 0;
            font-size: em(8, 16);
            left: em(10, 8);
            transform: translateY(-50%);
            text-transform: uppercase;
          }

          &--active {
            color: $color-main-1;
          }
        }

        &__error {
          margin-top: em(2);
          margin-left: em(11);
        }
      }
    }

    &--outlined-2-red {
      #{$parent} {
        &__field {
          border-color: $color-error;
        }
      }
    }

    &--outlined-2-orange {
      #{$parent} {
        &__field {
          &--active {
            border-color: $color-second-1 !important;
          }
        }

        &__placeholder {
          &--active {
            color: $color-gray-4;
          }
        }
      }
    }
  }
}
</style>
